import { Loader } from "components";
import { LocationGenerics } from "router/location";
import ParticipationOverview from "./sections/participation-overview";
import clsx from "clsx";
import moment from "moment";
import numeral from "numeral";
import { toast } from "react-hot-toast";
import { useMatch } from "react-location";
import { useOffer } from "hooks/data/use-offers";

export default function OfferOverView() {
  const { params } = useMatch<LocationGenerics>();
  const { loading, offer } = useOffer({
    offer_id: params.offer || "",
  });

  if (loading) return <Loader />;

  return (
    <div className="flex-1 px-3 sm:px-6 py-4 grid grid-cols-1 md:grid-cols-4 items-start gap-4 sm:gap-6">
      <div className="bg-card-500 md:col-span-2 rounded-xl shadow-lg overflow-hidden">
        <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-700/50">
          <div className="flex items-start sm:items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-lg sm:text-xl font-semibold text-primary truncate">
                Offer Information
              </h3>
              <p className="mt-1 text-xs sm:text-sm text-secondary truncate">
                Comprehensive details about the insurance offer
              </p>
            </div>
            <div className="flex-shrink-0">
              <span
                className={clsx(
                  "inline-flex items-center px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium",
                  {
                    "bg-indigo-400/10 text-indigo-400 ring-1 ring-indigo-400/30":
                      offer?.offer_status === "OPEN",
                    "bg-yellow-400/10 text-yellow-400 ring-1 ring-yellow-400/30":
                      offer?.offer_status === "PENDING",
                    "bg-teal-400/10 text-teal-400 ring-1 ring-teal-400/30":
                      offer?.offer_status === "CLOSED",
                  }
                )}
                aria-live="polite"
              >
                {offer?.offer_status}
              </span>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <InfoCard
              label="Policy Number"
              value={offer?.offer_detail?.policy_number}
              copy
            />
            <InfoCard
              className="sm:col-span-2"
              label="Reinsured"
              value={offer?.insurer?.insurer_company_name}
            />
            <InfoCard
              label="Class of Business"
              value={offer?.classofbusiness?.business_name}
            />

            <InfoCard label="Insured" value={offer?.offer_detail?.insured_by} />
            <InfoCard
              label="Period of Insurance"
              value={
                offer?.offer_detail?.period_of_insurance_from &&
                offer?.offer_detail?.period_of_insurance_to
                  ? `${moment(
                      offer.offer_detail.period_of_insurance_from
                    ).format("DD/MM/YYYY")} to ${moment(
                      offer.offer_detail.period_of_insurance_to
                    ).format("DD/MM/YYYY")}`
                  : null
              }
            />
            <InfoCard label="Rate" value={`${offer?.rate || 0}%`} />
            <InfoCard label="Commission" value={`${offer?.commission || 0}%`} />
            <InfoCard
              label="Facultative Offer"
              value={`${offer?.facultative_offer || 0}%`}
            />
            <InfoCard label="Brokerage" value={`${offer?.brokerage || 0}%`} />
            <InfoCard
              label="Premium"
              value={
                offer?.premium != null
                  ? `${offer?.offer_detail?.currency || ""} ${numeral(
                      offer?.premium
                    ).format("0,0.00")}`
                  : null
              }
            />
            <InfoCard
              label="Sum Insured"
              value={
                offer?.sum_insured != null
                  ? `${offer?.offer_detail?.currency || ""} ${numeral(
                      offer?.sum_insured
                    ).format("0,0.00")}`
                  : null
              }
            />
            <InfoCard
              label="Facultative Sum Insured"
              value={
                offer?.fac_sum_insured != null
                  ? `${offer?.offer_detail?.currency || ""} ${numeral(
                      offer?.fac_sum_insured
                    ).format("0,0.00")}`
                  : null
              }
            />
            <InfoCard
              label="Facultative Premium"
              value={
                offer?.fac_premium != null
                  ? `${offer?.offer_detail?.currency || ""} ${numeral(
                      offer?.fac_premium
                    ).format("0,0.00")}`
                  : null
              }
            />
          </div>
        </div>
      </div>
      <div className="md:col-span-2">
        <ParticipationOverview offer={offer} />
      </div>
    </div>
  );
}

const InfoCard = ({
  label,
  value,
  copy,
  link,
  className,
}: {
  label: string;
  value?: string | null;
  copy?: boolean;
  link?: boolean;
  className?: string;
}) => {
  const handleCopy = () => {
    if (value) {
      navigator.clipboard.writeText(value);
      toast.success("Copied to clipboard");
    }
  };

  const handleClick = () => {
    if (link && value) {
      window.open(value, "_blank");
    }
  };

  return (
    <div
      className={clsx(
        "bg-card-600/50 rounded-lg p-3 sm:p-4 backdrop-blur-sm w-full min-w-0",
        className
      )}
    >
      <dt className="text-xs sm:text-sm font-light text-tertiary truncate">
        {label}
      </dt>
      <dd className="mt-2 sm:mt-1 text-sm sm:text-base font-semibold text-primary flex flex-col sm:flex-row items-start sm:items-center gap-2">
        {link ? (
          <button
            onClick={handleClick}
            className="hover:underline cursor-pointer text-left min-w-0 break-words sm:truncate whitespace-normal sm:whitespace-nowrap"
            aria-label={`Open ${label}`}
          >
            {value || "N/A"}
          </button>
        ) : (
          <span className="min-w-0 break-words sm:truncate whitespace-normal sm:whitespace-nowrap">
            {value || "N/A"}
          </span>
        )}
        <div className="flex items-center gap-2 ml-0 sm:ml-auto">
          {copy && value && (
            <button
              onClick={handleCopy}
              className="p-2 sm:p-1 hover:bg-gray-700 rounded-md"
              title="Copy to clipboard"
              aria-label={`Copy ${label}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                />
              </svg>
            </button>
          )}
        </div>
      </dd>
    </div>
  );
};
